import React, { useEffect, useMemo, useState } from 'react'
import { FaAlignLeft, FaArrowUp, FaArrowDown, FaDownload, FaEdit, FaFileAlt, FaFileExcel, FaFileImage, FaFilePdf, FaFilePowerpoint, FaFileWord, FaLink, FaPlus, FaTrash, FaTimes, FaVideo, FaYoutube } from 'react-icons/fa'
import { Alert, Box, Button, ConfirmButton, FormControl, Heading, Icon, Input, Loading, SimpleGrid, SimpleModal, Stack, Text, Textarea, SearchableSelect, EmptyBox, Badge, EmptyCard } from '@/components/lib'
import api from '@/api'
import useStore from '@/store'
import utils, { isLikelyMp4Upload } from '@/utils/utils'
import FileChooser from '@/components/includes/FileChooser'
import RichText from '@/components/includes/RichText'
import RichTextViewer from '@/components/includes/RichTextViewer'

const TYPE_OPTIONS = [
  { value: 'file', label: 'File' },
  { value: 'video', label: 'Video' },
  { value: 'link', label: 'Link' },
  { value: 'text', label: 'Text' },
  { value: 'youtube', label: 'YouTube' }
]

function stripHtmlToText (html) {
  if (!html) return ''
  return String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
}

function typeIcon (type) {
  if (type === 'video') return FaVideo
  if (type === 'link') return FaLink
  if (type === 'text') return FaAlignLeft
  if (type === 'youtube') return FaYoutube
  return FaFileAlt
}

function guessFileIcon (mimeType, name) {
  const mt = (mimeType || '').toLowerCase()
  const n = (name || '').toLowerCase()
  const ext = n.includes('.') ? n.split('.').pop() : ''
  if (mt.startsWith('image/')) return FaFileImage
  if (mt === 'application/pdf' || ext === 'pdf') return FaFilePdf
  if (mt.includes('word') || ['doc', 'docx'].includes(ext)) return FaFileWord
  if (mt.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) return FaFileExcel
  if (mt.includes('powerpoint') || ['ppt', 'pptx'].includes(ext)) return FaFilePowerpoint
  return FaFileAlt
}

function isImageMime (mimeType) {
  return (mimeType || '').toLowerCase().startsWith('image/')
}

export default function ParticipantResources () {
  const { user, trial } = useStore()
  const trialId = trial?._id
  const canRead = utils.canReadTrial(user, trial)
  const canWrite = utils.canWriteTrial(user, trial)

  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(false)

  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newType, setNewType] = useState('file')
  const [newTitle, setNewTitle] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newLinkUrl, setNewLinkUrl] = useState('')
  const [newHtml, setNewHtml] = useState('<p></p>')
  const [newYoutubeUrl, setNewYoutubeUrl] = useState('')
  const [uploaded, setUploaded] = useState(null)

  const [editOpen, setEditOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editingResource, setEditingResource] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editLinkUrl, setEditLinkUrl] = useState('')
  const [editHtml, setEditHtml] = useState('<p></p>')
  const [editYoutubeUrl, setEditYoutubeUrl] = useState('')

  const empty = useMemo(() => (resources?.length || 0) === 0, [resources])

  function refresh () {
    if (!trialId || !canRead) return
    setLoading(true)
    api.participantResources.list(trialId, (d) => {
      setResources(d.resources || [])
      setLoading(false)
    }, () => setLoading(false))
  }

  useEffect(() => {
    refresh()
  }, [trialId, canRead])

  function resetCreate () {
    setNewType('file')
    setNewTitle('')
    setNewDescription('')
    setNewLinkUrl('')
    setNewHtml('<p></p>')
    setNewYoutubeUrl('')
    setUploaded(null)
    setCreating(false)
    setCreateOpen(false)
  }

  async function create () {
    if (!trialId || !canWrite) return
    setCreating(true)
    const payload = {
      type: newType,
      title: newTitle || null,
      description: newDescription || null
    }
    if (newType === 'link') {
      payload.linkUrl = (newLinkUrl || '').trim()
    } else if (newType === 'text') {
      payload.html = newHtml || ''
    } else if (newType === 'youtube') {
      payload.youtubeUrl = (newYoutubeUrl || '').trim()
    } else {
      payload.storedName = uploaded?.storedName
      payload.originalName = uploaded?.name
      payload.mimeType = uploaded?.mimeType
      payload.size = uploaded?.size
    }
    api.participantResources.create(trialId, payload, () => {
      utils.toast('success', 'Resource created')
      resetCreate()
      refresh()
    }, (err) => {
      setCreating(false)
      utils.toast('error', err.message || 'Unable to create resource')
    })
  }

  function remove (resourceId) {
    if (!trialId || !canWrite) return
    api.participantResources.delete(trialId, resourceId, () => {
      setResources(prev => (prev || []).filter(r => String(r._id) !== String(resourceId)))
      utils.toast('success', 'Resource deleted')
    }, (err) => utils.toast('error', err.message || 'Unable to delete resource'))
  }

  function openEdit (r) {
    if (!r || !canWrite) return
    setEditingResource(r)
    setEditTitle(r.title || '')
    setEditDescription(r.description || '')
    setEditLinkUrl(r.linkUrl || '')
    setEditHtml(r.html || '<p></p>')
    setEditYoutubeUrl(
      r.youtubeVideoId
        ? `https://www.youtube.com/watch?v=${r.youtubeVideoId}`
        : ''
    )
    setEditing(false)
    setEditOpen(true)
  }

  function closeEdit () {
    if (editing) return
    setEditOpen(false)
    setEditingResource(null)
    setEditTitle('')
    setEditDescription('')
    setEditLinkUrl('')
    setEditHtml('<p></p>')
    setEditYoutubeUrl('')
  }

  function saveEdit () {
    if (!trialId || !canWrite || !editingResource?._id) return
    setEditing(true)
    const payload = {
      title: editTitle || null,
      description: editDescription || null
    }
    if (editingResource?.type === 'link') payload.linkUrl = (editLinkUrl || '').trim()
    if (editingResource?.type === 'text') payload.html = editHtml || ''
    if (editingResource?.type === 'youtube') payload.youtubeUrl = (editYoutubeUrl || '').trim()
    api.participantResources.update(trialId, editingResource._id, payload, (updated) => {
      setResources(prev => (prev || []).map(r => String(r._id) === String(updated._id) ? updated : r))
      utils.toast('success', 'Resource updated')
      closeEdit()
    }, (err) => {
      setEditing(false)
      utils.toast('error', err.message || 'Unable to update resource')
    })
  }

  function move (r, direction) {
    const newIndex = (r.index || 0) + direction
    if (newIndex < 0 || newIndex >= resources.length) return
    api.participantResources.update(trialId, r._id, { index: newIndex }, () => {
      refresh()
    }, (err) => {
      utils.toast('error', err.message || 'Unable to move resource')
    })
  }

  if (!canRead) {
    return (
      <EmptyBox icon='lock' title='No access' description='You do not have permission to view participant resources in this trial.' />
    )
  }

  return (
    <Stack gap={4}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Box>
          <Heading size='md'>Participant resources</Heading>
          <Text fontSize='sm' color='fg.subtle'>These resources will be available via the mobile app to help participants during the study.</Text>
        </Box>
        {canWrite && (
          <Button colorPalette='primary' onClick={() => setCreateOpen(true)}>
            <Icon as={FaPlus} /> Add resource
          </Button>
        )}
      </Stack>

      {loading && <Loading />}

      {!loading && empty &&
        <Stack gap={5} alignItems='center'>
          <EmptyCard icon='files' title='Resources' description='It can be useful to provide participants with instructions, guidance or other materials to help them during the study. When created, Trialflare will make them available via the mobile app.' />
          <Box w='100%'>
            <EmptyBox
              title='No participant resources have been added yet'
              description='Add a file, link or text to get started.'
              action={canWrite && <Button colorPalette='primary' onClick={() => setCreateOpen(true)}>Add resource</Button>}
            />
          </Box>
        </Stack>}

      {!loading && !empty && (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={3}>
          {(resources || []).map((r, arrayIndex) => {
            const IconType = typeIcon(r.type)
            const href = r.type === 'link' ? r.linkUrl : r.url
            const textPreview = r.type === 'text' ? stripHtmlToText(r.html) : ''
            const label =
              r.title ||
              r.originalName ||
              (r.type === 'link' ? r.linkUrl : null) ||
              (r.type === 'text' && textPreview ? textPreview.slice(0, 80) + (textPreview.length > 80 ? '…' : '') : null) ||
              (r.type === 'youtube' ? 'YouTube video' : null) ||
              'Untitled'
            const isFirst = arrayIndex === 0
            const isLast = arrayIndex === resources.length - 1
            return (
              <Box key={r._id} borderWidth='1px' rounded='md' bg='bg.surface' p={3}>
                <Stack gap={2}>
                  <Stack direction='row' alignItems='flex-start' justifyContent='space-between'>
                    <Stack direction='row' alignItems='center' gap={2} minW={0}>
                      <Icon as={IconType} color='fg.subtle' />
                      <Text fontWeight='semibold' noOfLines={2}>{label}</Text>
                    </Stack>
                    <Stack direction='row' gap={1}>
                      {canWrite &&
                        <>
                          {!isFirst && <Button size='xs' variant='ghost' onClick={() => move(r, -1)} aria-label='Move up'><Icon as={FaArrowUp} /></Button>}
                          {!isLast && <Button size='xs' variant='ghost' onClick={() => move(r, 1)} aria-label='Move down'><Icon as={FaArrowDown} /></Button>}
                          <Button size='xs' variant='ghost' onClick={() => openEdit(r)} aria-label='Edit resource'>
                            <Icon as={FaEdit} />
                          </Button>
                          <ConfirmButton
                            trigger={<Button size='xs' variant='ghost' aria-label='Delete resource'><Icon as={FaTrash} /></Button>}
                            header='Delete this resource?'
                            body='Participants will no longer be able to access it.'
                            onConfirm={() => remove(r._id)}
                          />
                        </>}
                    </Stack>
                  </Stack>

                  <Stack direction='row' gap={2} alignItems='center'>
                    <Badge size='sm' variant='subtle'>{(r.type || '').toUpperCase()}</Badge>
                    {r.mimeType && <Text fontSize='xs' color='fg.muted'>{r.mimeType}</Text>}
                    {r.type === 'video' && !isLikelyMp4Upload({ mimeType: r.mimeType, name: r.originalName }) && <Badge colorPalette='orange' size='sm' variant='subtle'>Not MP4</Badge>}
                    {r.type === 'link' && href && <Text fontSize='xs' color='fg.muted'>{href}</Text>}
                    {r.type === 'youtube' && r.youtubeVideoId && (
                      <Text fontSize='xs' color='fg.muted' noOfLines={1}>ID: {r.youtubeVideoId}</Text>
                    )}
                  </Stack>

                  {r.type === 'text' && r.html && (
                    <Box borderWidth='1px' rounded='md' bg='bg.subtle' p={3} fontSize='sm'>
                      <RichTextViewer content={r.html} />
                    </Box>
                  )}

                  {r.type === 'youtube' && r.youtubeVideoId && (
                    <Box position='relative' w='100%' pt='56.25%' rounded='md' overflow='hidden' bg='black'>
                      <iframe
                        title='YouTube video'
                        src={`https://www.youtube.com/embed/${r.youtubeVideoId}`}
                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                        allowFullScreen
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 0
                        }}
                      />
                    </Box>
                  )}

                  {r.type === 'video' && r.url && (
                    <Box>
                      <video
                        src={r.url}
                        controls
                        preload='metadata'
                        style={{ width: '100%', borderRadius: 8 }}
                      />
                    </Box>
                  )}

                  {r.type === 'file' && isImageMime(r.mimeType) && r.url && (
                    <Box>
                      <img
                        src={r.url}
                        alt={label}
                        style={{ width: '100%', borderRadius: 8, display: 'block' }}
                      />
                    </Box>
                  )}

                  {r.description && (
                    <Text fontSize='sm' color='fg.subtle' noOfLines={4}>{r.description}</Text>
                  )}

                  {(r.type === 'file' || r.type === 'link') && href && (
                    <Stack direction='row' justifyContent='end'>
                      <Button
                        size='sm'
                        variant='outline'
                        colorPalette='primary'
                        asChild
                      >
                        <a href={href} target='_blank' rel='noreferrer'>
                          {r.type === 'file' ? 'Download file' : 'Open link'}
                        </a>
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Box>
            )
          })}
        </SimpleGrid>
      )}

      {canWrite && (
        <SimpleModal
          isOpen={createOpen}
          onClose={() => (creating ? null : resetCreate())}
          header='Add a participant resource'
          modalProps={{ size: 'lg' }}
          body={
            <Stack gap={4}>
              <FormControl label='Type'>
                <SearchableSelect
                  options={TYPE_OPTIONS}
                  value={newType}
                  onChange={(v) => {
                    setNewType(v)
                    setUploaded(null)
                    if (v === 'text') setNewHtml('<p></p>')
                    if (v === 'youtube') setNewYoutubeUrl('')
                  }}
                />
              </FormControl>

              <FormControl label='Title (optional)'>
                <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder='e.g. Travel expenses guide' />
              </FormControl>

              <FormControl label='Description (optional)'>
                <Textarea value={newDescription} onChange={e => setNewDescription(e.target.value)} placeholder='A short description for participants.' />
              </FormControl>

              {newType === 'link' && (
                <FormControl label='Link URL'>
                  <Input value={newLinkUrl} onChange={e => setNewLinkUrl(e.target.value)} placeholder='https://…' />
                </FormControl>
              )}

              {newType === 'text' && trialId && (
                <FormControl label='Content'>
                  <Box w='100%'>
                    <RichText
                      value={newHtml}
                      onChange={setNewHtml}
                      forType='trial'
                      forId={trialId}
                    />
                  </Box>
                </FormControl>
              )}

              {newType === 'youtube' && (
                <FormControl label='YouTube URL or video ID' helperText='Paste a watch, embed, shorts, or youtu.be link, or the 11-character video ID.'>
                  <Input
                    value={newYoutubeUrl}
                    onChange={e => setNewYoutubeUrl(e.target.value)}
                    placeholder='https://www.youtube.com/watch?v=…'
                  />
                </FormControl>
              )}

              {(newType === 'file' || newType === 'video') && (
                <Stack gap={2}>
                  {!uploaded &&
                    <FileChooser
                      forObject={trial}
                      forType='trial'
                      fileType={newType === 'video' ? 'video' : 'file'}
                      accept={newType === 'video' ? 'video/*' : undefined}
                      trigger={<Button colorPalette='primary' variant='outline' isLoading={creating}>Choose a {newType === 'video' ? 'video' : 'file'}</Button>}
                      onComplete={(f) => setUploaded(f)}
                      onError={(msg) => utils.toast('error', msg)}
                    />}

                  {uploaded?.expectedUrl && newType === 'video' && (
                    <Stack borderWidth='1px' rounded='md' bg='bg.subtle' p={2} gap={2}>
                      {!isLikelyMp4Upload({ mimeType: uploaded.mimeType, name: uploaded.name }) && (
                        <Alert status='warning' size='sm'>
                          We recommend converting this video to MP4 for best compatibility.
                        </Alert>
                      )}
                      <video
                        src={uploaded.expectedUrl}
                        controls
                        preload='metadata'
                        style={{ width: '100%', borderRadius: 8 }}
                      />
                      <Button onClick={() => setUploaded(null)} size='sm' variant='ghost'>Remove video</Button>
                    </Stack>
                  )}

                  {uploaded?.expectedUrl && newType === 'file' && isImageMime(uploaded?.mimeType) && (
                    <Stack borderWidth='1px' rounded='md' bg='bg.subtle' p={2}>
                      <img
                        src={uploaded.expectedUrl}
                        alt={uploaded?.name || 'Uploaded image'}
                        style={{ width: '100%', borderRadius: 8, display: 'block' }}
                      />
                      <Button onClick={() => setUploaded(null)} size='sm' variant='ghost'>Remove image</Button>
                    </Stack>
                  )}

                  {uploaded?.expectedUrl && newType === 'file' && !isImageMime(uploaded?.mimeType) && (
                    <Box borderWidth='1px' rounded='md' bg='bg.subtle' p={3}>
                      <Stack direction='row' alignItems='center' justifyContent='space-between' gap={3}>
                        <Stack direction='row' alignItems='center' gap={2} minW={0}>
                          <Icon as={guessFileIcon(uploaded?.mimeType, uploaded?.name)} color='fg.subtle' />
                          <Box minW={0}>
                            <Text fontWeight='semibold' fontSize='sm' noOfLines={1}>{uploaded?.name || 'File'}</Text>
                          </Box>
                        </Stack>
                        <Stack direction='row' gap={2}>
                          <Button
                            size='sm'
                            variant='outline'
                            colorPalette='primary'
                            asChild
                          >
                            <a href={uploaded.expectedUrl} target='_blank' rel='noreferrer'>
                              <Icon as={FaDownload} /> Download
                            </a>
                          </Button>
                          <Button onClick={() => setUploaded(null)} size='sm' variant='ghost'>
                            <Icon as={FaTimes} />
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          }
          footer={
            <Stack direction='row' justifyContent='end' gap={2}>
              <Button onClick={resetCreate} disabled={creating}>Cancel</Button>
              <Button
                colorPalette='primary'
                onClick={create}
                loading={creating}
                disabled={
                  (newType === 'link' && !(newLinkUrl || '').trim()) ||
                  ((newType === 'file' || newType === 'video') && !uploaded?.storedName) ||
                  (newType === 'text' && !stripHtmlToText(newHtml)) ||
                  (newType === 'youtube' && !(newYoutubeUrl || '').trim())
                }
              >
                Create
              </Button>
            </Stack>
          }
        />
      )}

      {canWrite && (
        <SimpleModal
          isOpen={editOpen}
          onClose={closeEdit}
          header='Edit resource'
          modalProps={{ size: 'lg' }}
          body={
            <Stack gap={4}>
              <FormControl label='Title (optional)'>
                <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder='e.g. Travel expenses guide' />
              </FormControl>

              <FormControl label='Description (optional)'>
                <Textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} placeholder='A short description for participants.' />
              </FormControl>

              {editingResource?.type === 'link' && (
                <FormControl label='Link URL'>
                  <Input value={editLinkUrl} onChange={e => setEditLinkUrl(e.target.value)} placeholder='https://…' />
                </FormControl>
              )}

              {editingResource?.type === 'text' && trialId && (
                <FormControl label='Content'>
                  <Box w='100%'>
                    <RichText
                      value={editHtml}
                      onChange={setEditHtml}
                      forType='trial'
                      forId={trialId}
                    />
                  </Box>
                </FormControl>
              )}

              {editingResource?.type === 'youtube' && (
                <FormControl label='YouTube URL or video ID'>
                  <Input
                    value={editYoutubeUrl}
                    onChange={e => setEditYoutubeUrl(e.target.value)}
                    placeholder='https://www.youtube.com/watch?v=…'
                  />
                </FormControl>
              )}

              {(editingResource?.type === 'file' || editingResource?.type === 'video') && (
                <Text fontSize='sm' color='fg.subtle'>
                  The uploaded {editingResource?.type === 'video' ? 'video' : 'file'} cannot be changed after creation.
                </Text>
              )}
            </Stack>
          }
          footer={
            <Stack direction='row' justifyContent='end' gap={2}>
              <Button onClick={closeEdit} disabled={editing}>Cancel</Button>
              <Button
                colorPalette='primary'
                onClick={saveEdit}
                loading={editing}
                disabled={
                  (editingResource?.type === 'link' && !(editLinkUrl || '').trim()) ||
                  (editingResource?.type === 'text' && !stripHtmlToText(editHtml)) ||
                  (editingResource?.type === 'youtube' && !(editYoutubeUrl || '').trim())
                }
              >
                Save
              </Button>
            </Stack>
          }
        />
      )}
    </Stack>
  )
}
